import { useEffect } from 'react';
import { Music, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function SpotifyConnectCard({ status, onChange }) {
  // status: {configured, connected, spotify_user_id, display_name, connected_at, scope}
  useEffect(() => {
    // After OAuth round-trip, the callback redirects to /profile?spotify=connected|error
    const params = new URLSearchParams(window.location.search);
    const sp = params.get('spotify');
    if (!sp) return;
    if (sp === 'connected') {
      toast.success('Spotify connected');
    } else if (sp === 'error') {
      toast.error(`Spotify connect failed: ${params.get('reason') || 'unknown'}`);
    }
    // clean URL
    window.history.replaceState({}, '', window.location.pathname);
    onChange?.();
  }, [onChange]);

  const handleConnect = async () => {
    try {
      const { data } = await api.get('/spotify/connect-url');
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start Spotify connect');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Spotify? Your playlists stay on Spotify; GroovLens just forgets the token.')) return;
    try {
      await api.post('/spotify/disconnect');
      toast.success('Spotify disconnected');
      onChange?.();
    } catch (e) {
      toast.error('Failed');
    }
  };

  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
        <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center">
          <AlertCircle className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold">Spotify not configured</div>
          <div className="text-xs text-muted-foreground">Server missing Spotify keys</div>
        </div>
      </div>
    );
  }
  const connected = status.connected;
  return (
    <div
      className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card"
      data-testid="spotify-connect-card"
    >
      <div className="w-10 h-10 rounded-lg bg-[#1DB954]/15 grid place-items-center">
        <Music className="w-5 h-5 text-[#1DB954]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold flex items-center gap-2">
          Spotify
          {connected && (
            <Badge className="bg-[#1DB954]/15 text-[#1DB954] hover:bg-[#1DB954]/15 border border-[#1DB954]/30 text-[10px]">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {connected
            ? `${status.display_name || status.spotify_user_id} · unlock playlist actions + BPM/Key`
            : 'Connect to unlock Add-to-Playlist + audio features'}
        </div>
      </div>
      {connected ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDisconnect}
          className="rounded-lg"
          data-testid="spotify-disconnect-button"
        >
          Disconnect
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={handleConnect}
          className="rounded-lg bg-[#1DB954] hover:bg-[#1ed760] text-black"
          data-testid="spotify-connect-button"
        >
          Connect
        </Button>
      )}
    </div>
  );
}
